import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Input, Select } from '@/shared/ui';
import { track, type AuthOrigem } from '@/lib/analytics';
import {
  MEDICAL_SPECIALTIES,
  PHONE_MASK_LENGTH,
  PRIVACY_POLICY_URL,
  isFullName,
  isValidPhone,
  maskPhone,
  type MedicalSpecialty,
  type ProfileDraft,
  type ProfileFormInput,
} from '../../domain/entities/auth.entity';
import { useLeadCapture } from '@/modules/lead/presentation/hooks/use-lead-capture';
import { useAuth } from '../hooks/use-auth';

interface CompleteProfileFormProps {
  /** De onde a pessoa chegou: vai no evento de lead. */
  origem: AuthOrigem;
  /**
   * O que o cadastro já tinha coletado antes do "este e-mail já tem conta".
   * Com ele completo, o formulário **se envia sozinho** ao montar — a pessoa
   * já preencheu tudo uma vez — e só aparece se o envio falhar.
   */
  draft?: ProfileDraft;
  onSuccess: () => void;
}

/**
 * O cadastro desta plataforma para quem **já tem conta** na Primum, mas não
 * tem perfil aqui (veio de outra plataforma, ou entrou com o Google): nome,
 * telefone e especialidade pretendida. Sem e-mail e sem senha — a conta já existe, e o
 * back tira o e-mail do token.
 *
 * **O aceite dos termos**: quem chegou pelo cadastro (com `draft`) já aceitou
 * nele, e o aceite já foi registrado lá. Quem chegou só pelo login aceita aqui
 * — é a primeira vez nesta plataforma — e o registro sai, como no cadastro,
 * antes e sem esperar.
 */
export function CompleteProfileForm({
  origem,
  draft,
  onSuccess,
}: CompleteProfileFormProps) {
  const auth = useAuth();
  const { captureSignUp } = useLeadCapture();
  const complete = useMutation({
    mutationFn: (input: ProfileFormInput) => auth.completeRegistration(input),
    onSuccess: () => {
      // Um lead novo **para esta plataforma**: a conta já existia, então não é
      // `sign_up` — mas é a primeira vez que a pessoa se cadastra aqui.
      track({ event: 'generate_lead', lead_source: origem });
      onSuccess();
    },
  });

  const [fullName, setFullName] = useState(
    () => draft?.fullName ?? auth.user?.name ?? '',
  );
  const [phone, setPhone] = useState(() => maskPhone(draft?.phone ?? ''));
  const [intendedSpecialty, setIntendedSpecialty] = useState<string>(
    () => draft?.intendedSpecialty ?? '',
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const askTerms = draft === undefined;
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);

  // Rascunho completo: envia uma vez ao montar. A ref segura o duplo efeito do
  // StrictMode, que mandaria o cadastro duas vezes.
  const [isAutoSubmit] = useState(
    () =>
      draft !== undefined &&
      isFullName(draft.fullName) &&
      isValidPhone(draft.phone),
  );
  const autoSubmitted = useRef(false);
  useEffect(() => {
    if (autoSubmitted.current || !isAutoSubmit || !draft) return;
    autoSubmitted.current = true;
    complete.mutate(draft);
    // Só na montagem: depois disso quem decide é a pessoa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const nameIncomplete = !isFullName(fullName);
    const phoneIncomplete = !isValidPhone(phone);
    setNameError(nameIncomplete ? 'Informe seu nome e sobrenome.' : null);
    setPhoneError(
      phoneIncomplete ? 'Informe um telefone válido com DDD.' : null,
    );
    const termsMissing = askTerms && !acceptedTerms;
    setTermsError(
      termsMissing ? 'Você precisa aceitar a política de privacidade.' : null,
    );
    if (nameIncomplete || phoneIncomplete || termsMissing) return;

    const cleanName = fullName.trim().replace(/\s+/g, ' ');
    if (askTerms && auth.user?.email) {
      captureSignUp({ email: auth.user.email, fullName: cleanName, phone });
    }

    complete.mutate({
      fullName: cleanName,
      phone,
      intendedSpecialty: intendedSpecialty as MedicalSpecialty,
    });
  };

  // Enviando sozinho: não há o que a pessoa preencher, só esperar.
  if (isAutoSubmit && (complete.isIdle || complete.isPending)) {
    return (
      <p className="notice" role="status">
        Concluindo seu cadastro…
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="form-stack" noValidate>
      <Input
        id="profileFullName"
        label="Nome completo"
        required
        maxLength={241}
        autoComplete="name"
        value={fullName}
        onChange={(e) => {
          setFullName(e.target.value);
          setNameError(null);
        }}
        error={nameError ?? undefined}
        placeholder="Seu nome e sobrenome"
      />

      <Input
        id="profilePhone"
        label="Telefone com WhatsApp"
        type="tel"
        required
        maxLength={PHONE_MASK_LENGTH}
        autoComplete="tel"
        value={phone}
        onChange={(e) => {
          setPhone(maskPhone(e.target.value));
          setPhoneError(null);
        }}
        error={phoneError ?? undefined}
        placeholder="(00) 00000-0000"
      />

      <Select
        id="profileIntendedSpecialty"
        label="Especialidade pretendida"
        required
        placeholder="Selecione uma opção"
        value={intendedSpecialty}
        onChange={(e) => setIntendedSpecialty(e.target.value)}
        options={MEDICAL_SPECIALTIES.map((option) => ({
          value: option,
          label: option,
        }))}
      />

      {askTerms && (
        <div>
          <label className="field-check">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => {
                setAcceptedTerms(e.target.checked);
                if (e.target.checked) setTermsError(null);
              }}
            />
            <span>
              Li e aceito a{' '}
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Política de Privacidade
              </a>
              .
            </span>
          </label>
          {termsError && <p className="field-error">{termsError}</p>}
        </div>
      )}

      {complete.isError && (
        <div className="alert" role="alert">
          Não conseguimos concluir seu cadastro agora. Confira os dados e tente
          de novo.
        </div>
      )}

      <Button
        type="submit"
        disabled={complete.isPending || intendedSpecialty === ''}
      >
        {complete.isPending ? 'Concluindo…' : 'Concluir cadastro'}
      </Button>
    </form>
  );
}
